import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import i18next, { t } from 'i18next';
import React, { useContext, useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { GoogleAnalytics } from '../../../classes/GoogleAnalytics';
import { TranslationHelper } from '../../../classes/TranslationHelper';
import { PageNames } from '../../../Constants/Common';
import { TyreRepairRequestContext } from '../../../context/TyreRepairRequestContext';
import { CommonValidation } from '../../common/commonValidation/commonValidation';
import ConfirmAlert from '../../common/confirmAlert/ConfirmAlert';
import ErrorSummary from '../../common/ErrorSummary/ErrorSummary';
import FormInput from '../../common/FormInput';
import ModalDialog from '../../common/ModalDialog';
import TyreSpecificForm from './TyreSpecificForm';
import CarComponent from '../alloyRepairRequest/CarComponent';
import { TyreProcessStages } from './TyreRepairRequestTypes';

export type TyreSelectionProps = {
    cancelConfirmText: string;
};

const TyreSelection = ({
    cancelConfirmText
}: TyreSelectionProps): JSX.Element => {
    const [showDialog, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);

    const {
        tyreRepairRequest,
        onTyreSelected,
        onUploadImageChange,
        onChangeDamageType,
        onTyreValueChange,
        onCancelRepairRequest,
        onCancelUndo,
        onContinueToRepairAddress,
        onChangeStage,
        onCancelClick,
        onFinishAddingTyres,
        onDeleteRepairRequest
    } = useContext(TyreRepairRequestContext);

    const {
        currentRepairRequest: repairRequest,
        updateTyre,
        selectedTyres,
        showCancelAlert
    } = tyreRepairRequest;

    useEffect(() => {
        if (CommonValidation.isNotEmpty(updateTyre)) {
            onTyreClick(updateTyre);
        }

        GoogleAnalytics.setPageView(PageNames.TyreSelection);
    }, []);

    const onCloseConfirmation = (): void => {
        setShowConfirmation(false);
    };
    const onCancel = (): void => {
        setShowModal(false);
        setFormErrors('');
    };

    const [formErrors, setFormErrors] = useState('');

    const onContinueClick = () => {
        if (selectedTyres && selectedTyres.length > 0) {
            onContinueToRepairAddress();
        } else {
            setFormErrors(t('select_tyre_for_repair'));
            GoogleAnalytics.trackEventsGoogleAnalytics(
                'Button click',
                'Alloy wheel button clicks',
                'Clicked continue without selecting a wheel'
            );
        }
    };

    const getWheelListItem = (wheel: string) => {
        return (
            <li
                key={wheel}
                className='list-group-item d-flex justify-content-between align-items-start'>
                {TranslationHelper.getTranslatedText(wheel)}
                <span className='badge rounded-pill' style={{ color: 'green' }}>
                    <FontAwesomeIcon icon={faCheck} size='2x' />
                </span>
            </li>
        );
    };

    const getClickedWheels = () => {
        return (
            <ul className='list-group list-group-flush'>
                {selectedTyres.map((wheel) => getWheelListItem(wheel))}
            </ul>
        );
    };

    const onTyreClick = (tyre: string) => {
        setShowModal(true);
        setModalTitle(tyre);
        onTyreSelected(tyre);
    };

    const cancelButtonText =
        selectedTyres.length > 1
            ? t('cancel_repair_requests')
            : t('cancel_repair_request');

    const onSave = (): void => {
        const isFormValid = onFinishAddingTyres();
        if (isFormValid === true) setShowModal(false);
    };

    const onShowConfirmation = (): void => {
        setShowConfirmation(true);
    };

    const onDeleteTyre = (): void => {
        setShowConfirmation(false);
        setShowModal(false);
        onDeleteRepairRequest(repairRequest?.damagedTyre || 'Unknown Tyre');
    };

    return (
        <>
            <div className='card page-content'>
                <div className='card-body page-content-body'>
                    <FormInput
                        id='wheel-selection-forminput'
                        label={t(
                            'tyre_repair_request.step_2_tyre_selection_title'
                        )}
                        errorText={formErrors}>
                        <CarComponent
                            selectedWheels={selectedTyres}
                            onWheelSelected={onTyreClick}
                            isLeftHandDrive={
                                i18next.language.toLowerCase() === 'us'
                            }
                        />
                    </FormInput>
                </div>
                <div className='card-footer page-content-footer'>
                    {CommonValidation.isNotEmpty(formErrors) && (
                        <ErrorSummary
                            title={t('tyre_selection_errors')}
                            errors={[formErrors]}
                            className='card-error'
                        />
                    )}
                </div>
                {getClickedWheels()}
            </div>
            <div className='bottom-navigation'>
                <button
                    id='btnContinueSelection'
                    data-testid='primary-button'
                    className='btn btn-primary progress-button'
                    onClick={onContinueClick}>
                    {t('button_label.continue')}
                </button>
                <button
                    id='btnBackSelection'
                    className='btn btn-secondary progress-button'
                    onClick={() => onChangeStage(TyreProcessStages.General)}>
                    {t('back')}
                </button>
                <button
                    id='btnCancelSelection'
                    data-testid='danger-button'
                    className='btn btn-danger progress-button'
                    onClick={onCancelClick}>
                    {cancelButtonText}
                </button>
            </div>
            <ConfirmAlert
                showAlert={showCancelAlert}
                onConfirm={onCancelRepairRequest}
                onCancel={onCancelUndo}
                alertText={cancelConfirmText}
            />
            <Modal isOpen={showDialog} className='alloy-wheel-selection'>
                <ModalHeader toggle={onCancel}>{modalTitle}</ModalHeader>
                <ModalBody className='alloy-wheel-form'>
                    {showDialog && (
                        <TyreSpecificForm
                            repairRequest={repairRequest}
                            onChange={onTyreValueChange}
                            onChangeDamageType={onChangeDamageType}
                            onUploadImageChange={onUploadImageChange}
                            errorSummaryTitle={`${modalTitle} Errors`}
                        />
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        className='btn btn-primary progress-button'
                        color='primary'
                        onClick={onSave}>
                        {t('button_label.confirm')}
                    </Button>
                    <Button
                        data-testid='danger-button'
                        className='btn btn-danger progress-button'
                        onClick={onShowConfirmation}>
                        {t('button_label.delete')}
                    </Button>
                    <Button
                        data-testid='secondary-button'
                        className='btn btn-secondary progress-button'
                        onClick={onCancel}>
                        {t('button_label.cancel')}
                    </Button>
                </ModalFooter>
            </Modal>
            <ModalDialog
                showDialog={showConfirmation}
                title={t('confirm_delete')}
                primaryButton={{
                    text: t('confirm'),
                    disabled: false,
                    onClick: onDeleteTyre
                }}
                secondaryButton={{
                    text: t('cancel'),
                    onClick: onCloseConfirmation
                }}
                onCloseClick={onCloseConfirmation}>
                {t('delete_tyre_request')}
            </ModalDialog>
        </>
    );
};

export default TyreSelection;
